import React from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from 'recharts';
import { useMaterialAvailability } from '../../hooks';

const CommodityChart = ({
  materialCode,
  data: propData,
  onValue: propOnValue,
  offValue: propOffValue,
  description: propDescription
}) => {
  const { t, i18n } = useTranslation();
  // Fetch availability data from API
  const { data: availability, loading, error } = useMaterialAvailability(materialCode);

  // Use API data if available, otherwise fall back to props
  const data = propData ?? (availability?.data || [
    { year: '2020', on: 95, off: 98 },
    { year: '2021', on: 88, off: 95 },
    { year: '2022', on: 92, off: 90 },
    { year: '2023', on: 85, off: 88 },
    { year: '2024', on: 80, off: 85 },
    { year: '2025', on: 75, off: 82 },
    { year: '2026', on: 78, off: 78 }
  ]);

  const onValue = propOnValue ?? (availability?.onValue || '3.215.152');
  const offValue = propOffValue ?? (availability?.offValue || '3.215.152');
  const description = propDescription ?? (availability?.description || t("charts.commodity.fallbackDescription"));

  // Build chart data with stable index to avoid duplicate-year hover issues
  const chartData = (data || []).map((d, idx) => ({
    ...d,
    idx,
    label: d.year || `${idx + 1}`,
  }));

  const formatNumber = (v) => {
    const num = typeof v === 'number' ? v : parseFloat(v);
    if (!Number.isFinite(num)) return v;
    const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
      ? 'de-DE'
      : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('zh')
        ? 'zh-CN'
        : 'en-US';
    return num.toLocaleString(locale, { maximumFractionDigits: 0 });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const point = payload[0]?.payload;
    const year = point?.year;
    const on = point?.on;
    const off = point?.off;

    return (
      <div style={{
        background: 'rgba(17, 24, 39, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: 8,
        padding: '10px 12px',
        boxShadow: '0 6px 12px rgba(0,0,0,0.4)',
        color: '#fff',
        minWidth: 160,
      }}>
        <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 14 }}>{t('charts.commodity.year', { year })}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
          <span style={{ color: '#6366F1' }}>{t('charts.commodity.on')}</span>
          <span>{formatNumber(on)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: '#3B82F6' }}>{t('charts.commodity.off')}</span>
          <span>{formatNumber(off)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="commodity-availability-container">
      {/* Header */}
      <div className="commodity-header">
        <h2 className="commodity-title">
          {t('charts.commodity.title')}
        </h2>

        {/* Data Labels */}
        <div className="commodity-data-labels">
          <div className="commodity-label">
            {t('charts.commodity.onValue', { value: onValue })}
          </div>
          <div className="commodity-label">
            {t('charts.commodity.offValue', { value: offValue })}
          </div>
        </div>
      </div>

        {/* Chart Area */}
        <div className="commodity-chart-area">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
              <defs>
              <linearGradient id="onGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366F1" stopOpacity={0.6}/>
                <stop offset="100%" stopColor="#8687c2ff" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="offGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#96accfff" stopOpacity={0.4}/>
                <stop offset="100%" stopColor="#8687c2ff" stopOpacity={0.05}/>
              </linearGradient>
            </defs>

            {/* Grid lines - only vertical dotted */}
            <CartesianGrid
              strokeDasharray="4 4"
              stroke="rgba(255, 255, 255, 0.2)"
                strokeWidth={1}
                horizontal={false}
                vertical={true}
              />

              <Tooltip
                content={(props) => <CustomTooltip {...props} />}
                cursor={{ stroke: 'rgba(255,255,255,0.5)', strokeDasharray: '4 4', strokeWidth: 1 }}
              />

              <XAxis
                dataKey="idx"
                type="number"
                domain={[0, Math.max(0, chartData.length - 1)]}
                ticks={chartData.map((d) => d.idx)}
                axisLine={true}
                tickLine={true}
                tick={{ fontSize: 12, fill: '#FFFFFF' }}
                interval="preserveStartEnd"
                tickFormatter={(v) => chartData.find((d) => d.idx === v)?.year || chartData[v]?.label || v}
              />
            <YAxis
              orientation="left"
              axisLine={true}
              tickLine={true}
              tick={{ fontSize: 12, fill: '#FFFFFF' }}
              domain={[(dataMin) => {
                // Add 10% padding below minimum, but ensure minimum of 0
                const padding = Math.abs(dataMin) * 0.1;
                return Math.max(0, Math.floor(dataMin - padding));
              }, (dataMax) => {
                // Add 10% padding above maximum
                const padding = Math.abs(dataMax) * 0.1;
                return Math.ceil(dataMax + padding);
              }]}
              tickCount={5}
              tickFormatter={(value) => value}
            />

            {/* Off area (lighter blue, background) */}
            <Area
              type="linear"
              dataKey="off"
              stroke="#3B82F6"
              strokeWidth={2}
              fill="url(#offGradient)"
              fillOpacity={1}
            />

            {/* On area (darker blue, foreground) */}
            <Area
              type="linear"
              dataKey="on"
              stroke="#6366F1"
              strokeWidth={2}
              fill="url(#onGradient)"
              fillOpacity={1}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Description */}
      <p className="commodity-description">
        {description}
      </p>
    </div>
  );
};

export default CommodityChart;
